import {Button, Divider, Stack, Typography} from '@mui/material';
import React from 'react';
import {useIsMobile} from '../../hooks';

export interface BaseActionButton {
  testId?: string;
  color?:
    | 'inherit'
    | 'error'
    | 'warning'
    | 'success'
    | 'info'
    | 'primary'
    | 'secondary';
  variant?: 'text' | 'outlined' | 'contained';
  title: string;
  loading?: boolean;
  onClick?: () => void;
  hidden?: boolean;
  disabled?: boolean;
  type?: 'button' | 'submit';
  selectionSummary?: {
    title: string;
    subtitle: string;
    visible?: boolean;
  };
}

export interface BaseActionsProps {
  actions?: BaseActionButton[];
}

export const BaseFullPageActions: React.FC<BaseActionsProps> = ({actions}) => {
  const isMobile = useIsMobile();
  const filteredActions = actions?.filter((a) => !a.hidden);
  return (
    <Stack
      width={'100%'}
      sx={(theme) => ({
        columnGap: 2,
        flexDirection: isMobile ? 'column' : 'row',
        flexShrink: 0,
        backgroundColor: theme.palette.background.default,
        px: {xs: 0, md: 2},
      })}>
      {filteredActions?.map((a) => {
        if (isMobile) {
          return (
            <React.Fragment key={a.title}>
              {a?.selectionSummary?.visible && (
                <SelectionSummary selectionSummary={a?.selectionSummary} />
              )}
              <Divider />
              <Button
                data-testid={a.testId}
                fullWidth
                sx={{p: 3, borderRadius: 0}}
                color={a.color ?? 'secondary'}
                variant={'text'}
                onClick={a.onClick}
                disabled={a.disabled}
                type={a.type ?? 'button'}>
                {a.title}
              </Button>
            </React.Fragment>
          );
        }
        return (
          <Stack key={a.title} width="100%">
            {a?.selectionSummary?.visible && (
              <SelectionSummary selectionSummary={a?.selectionSummary} />
            )}
            <Button
              data-testid={a.testId}
              fullWidth
              size="large"
              key={a.title}
              variant={a.variant ?? 'contained'}
              color={a.color ?? 'secondary'}
              onClick={a.onClick}
              disabled={a.disabled}
              type={a.type ?? 'button'}>
              {a.title}
            </Button>
          </Stack>
        );
      })}
    </Stack>
  );
};

type SelectionSummaryProps = {
  selectionSummary?: {
    title: string;
    subtitle: string;
  };
};
const SelectionSummary: React.FC<SelectionSummaryProps> = ({
  selectionSummary,
}) => {
  if (!selectionSummary) return null;
  return (
    <>
      <Divider />
      <Stack my={2} spacing={1} alignItems={'center'}>
        <Typography variant="body2" color="secondary">
          {selectionSummary.title}
        </Typography>
        <Typography variant="h3" fontWeight={'bold'}>
          {selectionSummary.subtitle}
        </Typography>
      </Stack>
    </>
  );
};
